/**
 * The Web Speech API, typed just enough for capture. Safari/iOS and Chrome
 * expose `webkitSpeechRecognition`; Firefox has none, so capture falls back
 * to typing. Recognition runs in the browser/OS; nothing extra is sent to
 * the Vitals server beyond the final text.
 */

export interface SpeechResultLike {
  isFinal: boolean;
  0: { transcript: string };
}

export interface SpeechEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechResultLike>;
}

export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export type SpeechCtor = new () => SpeechRecognitionLike;

export function speechCtor(): SpeechCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Final text so far, plus the live (interim) tail. */
export function transcriptOf(e: SpeechEventLike): { final: string; interim: string } {
  let final = '';
  let interim = '';
  for (let i = 0; i < e.results.length; i++) {
    const r = e.results[i]!;
    if (r.isFinal) final += r[0].transcript;
    else interim += r[0].transcript;
  }
  return { final: final.trim(), interim: interim.trim() };
}

/** Plain-English reason for a recognition error code. */
export function speechErrorText(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access is off for this site — type it instead.';
    case 'no-speech':
      return "Didn't catch anything. Tap the mic to try again.";
    case 'audio-capture':
      return 'No microphone found — type it instead.';
    case 'network':
      return 'Speech needs a connection right now — type it instead.';
    default:
      return 'Voice stopped — type it instead.';
  }
}
